import type { PartnerType } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { titleCasePtBR } from "@/lib/name-case";
import { PARTNER_TYPE_META, formatDoc, formatPhone } from "../partner-meta";
import { Field, Input, Section, UfSelect } from "./campos";
import type { FormularioDoCadastro } from "./use-cadastro";

const TIPOS: PartnerType[] = ["DENTIST", "LAB", "RADIOLOGY", "CLINIC_NETWORK", "SUPPLIER"];
const COM_FANTASIA = new Set<PartnerType>(["LAB", "RADIOLOGY", "CLINIC_NETWORK", "SUPPLIER"]);
const CLIENTES = new Set<PartnerType>(["DENTIST", "CLINIC_NETWORK"]);
const cartao = "rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card";

/**
 * As seções do cadastro. No diálogo de criar, empilhadas; na aba Dados do perfil (\`emCartoes\`),
 * um cartão por seção em duas colunas, como a ficha do paciente.
 */
export function CamposDoCadastro({
  values,
  setValues,
  travado,
  podeEditar,
  emCartoes = false,
  autoFocus = false,
}: {
  values: FormularioDoCadastro;
  setValues: (v: FormularioDoCadastro) => void;
  travado: boolean;
  podeEditar: boolean;
  emCartoes?: boolean;
  autoFocus?: boolean;
}) {
  const mudar = (parcial: Partial<FormularioDoCadastro>) => setValues({ ...values, ...parcial });
  const sec = cn(emCartoes && cartao);

  return (
    <div className={emCartoes ? "grid gap-4 md:grid-cols-2" : "space-y-3"}>
      <Section title="Categoria" className={cn(sec, emCartoes && "md:col-span-2")}>
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5">
          {TIPOS.map((t) => {
            const meta = PARTNER_TYPE_META[t];
            const ativo = values.type === t;
            return (
              <button
                key={t}
                type="button"
                disabled={!podeEditar}
                onClick={() => mudar({ type: t })}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-lg border p-2 transition-all disabled:cursor-not-allowed",
                  ativo ? "border-brand-500 bg-brand-50 dark:border-primary dark:bg-primary/10" : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-border dark:bg-background",
                )}
                title={meta.label}
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-md" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
                  <meta.icon className="h-3 w-3" />
                </span>
                <span className={cn("truncate text-[10px] font-semibold", ativo ? "text-brand-900 dark:text-primary" : "text-zinc-700 dark:text-foreground/80")}>{meta.label}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Identificação" className={sec}>
        <Field label={values.type === "DENTIST" ? "Nome *" : "Razão social *"} disabled={travado}>
          <Input value={values.name} onChange={(v) => mudar({ name: titleCasePtBR(v) })} autoFocus={autoFocus} disabled={travado} />
        </Field>
        {COM_FANTASIA.has(values.type) && (
          <Field label="Nome fantasia" disabled={!podeEditar}>
            <Input value={values.tradeName} onChange={(v) => mudar({ tradeName: titleCasePtBR(v) })} disabled={!podeEditar} />
          </Field>
        )}
        <Field label="CPF / CNPJ" disabled={travado}>
          <Input
            value={formatDoc(values.document) || values.document}
            onChange={(v) => mudar({ document: v.replace(/\D/g, "") })}
            placeholder="000.000.000-00 ou 00.000.000/0000-00"
            disabled={travado}
          />
        </Field>
        {values.type === "DENTIST" && (
          <div className="grid grid-cols-3 gap-2">
            <Field label="CRO" disabled={travado}>
              <Input value={values.croNumber} onChange={(v) => mudar({ croNumber: v })} disabled={travado} />
            </Field>
            <Field label="UF" disabled={travado}>
              <UfSelect value={values.croState} onChange={(v) => mudar({ croState: v })} disabled={travado} />
            </Field>
            <Field label="Especialidade" disabled={!podeEditar}>
              <Input value={values.specialty} onChange={(v) => mudar({ specialty: v })} disabled={!podeEditar} />
            </Field>
          </div>
        )}
      </Section>

      <Section title="Contato" className={sec}>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Telefone" disabled={travado}>
            <Input
              type="tel"
              value={formatPhone(values.phone) || values.phone}
              onChange={(v) => mudar({ phone: v.replace(/\D/g, "") })}
              placeholder="(11) 98765-4321"
              disabled={travado}
            />
          </Field>
          <Field label="E-mail" disabled={!podeEditar}>
            <Input type="email" value={values.email} onChange={(v) => mudar({ email: v })} disabled={!podeEditar} />
          </Field>
          {CLIENTES.has(values.type) && (
            <Field label="E-mail da cobrança" className="col-span-2" disabled={!podeEditar}>
              <Input
                type="email"
                value={values.emailDeCobranca}
                onChange={(v) => mudar({ emailDeCobranca: v })}
                placeholder="Se o extrato for para outro e-mail (o financeiro da clínica, por exemplo)"
                disabled={!podeEditar}
              />
            </Field>
          )}
        </div>
      </Section>

      <Section title="Endereço" className={sec}>
        <div className="grid grid-cols-3 gap-2">
          <Field label="CEP" disabled={travado}>
            <Input value={values.zipCode} onChange={(v) => mudar({ zipCode: v })} placeholder="00000-000" disabled={travado} />
          </Field>
          <Field label="Cidade" className="col-span-2" disabled={travado}>
            <Input value={values.city} onChange={(v) => mudar({ city: v })} disabled={travado} />
          </Field>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Endereço" className="col-span-3" disabled={travado}>
            <Input value={values.address} onChange={(v) => mudar({ address: v })} disabled={travado} />
          </Field>
          <Field label="Número" disabled={travado}>
            <Input value={values.addressNumber} onChange={(v) => mudar({ addressNumber: v })} disabled={travado} />
          </Field>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Complemento" className="col-span-2" disabled={travado}>
            <Input value={values.addressComplement} onChange={(v) => mudar({ addressComplement: v })} disabled={travado} />
          </Field>
          <Field label="Bairro" disabled={travado}>
            <Input value={values.neighborhood} onChange={(v) => mudar({ neighborhood: v })} disabled={travado} />
          </Field>
          <Field label="UF" disabled={travado}>
            <UfSelect value={values.state} onChange={(v) => mudar({ state: v })} disabled={travado} />
          </Field>
        </div>
      </Section>

      <Section title="Observações" className={sec}>
        <Field label="Notas" disabled={!podeEditar}>
          <textarea
            value={values.notes}
            onChange={(e) => mudar({ notes: e.target.value })}
            disabled={!podeEditar}
            rows={3}
            className="w-full resize-none bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
            placeholder="Informações adicionais sobre o parceiro..."
          />
        </Field>
      </Section>
    </div>
  );
}
